import { Button, Heading, Spacer, useDisclosure } from '@chakra-ui/react'
import React, { useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../ui-kit'
import { BaseLayout } from '..'
import PurgeCacheModal from '../../components/PurgeCache/PurgeCacheModal'
import PurgeCacheTable from '../../components/PurgeCache/PurgeCacheTable'
import type { PurgeCacheResponse } from '../../interfaces'
import { UserContext } from '../../providers'
import { MainContentHeadingBox, MainContentWrapper } from '../Base/MainContent'
import PermissionGuard from '../../components/PermissionGuard'
import { PermissionsEnum } from '../../../utils'
import useToast from '../../hooks/useToast'

const PurgeCacheLayout = (): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()

  const { user } = useContext(UserContext)

  const [data, setData] = useState<PurgeCacheResponse[]>()

  const { request, loading } = useFetch<PurgeCacheResponse[]>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const getData = async (): Promise<void> => {
    if (user?.clientNumber) {
      const response = await request.get(
        `/api/clients/${user.clientNumber}/purge-cache`,
      )

      if (request.error) {
        toast({
          description: t('error_fetching_data', 'Error in fetching data. Try refreshing the page.'),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else {
        setData(response)
      }
    }
  }

  const {
    isOpen: isOpenPurgeCache,
    onOpen: onOpenPurgeCache,
    onClose: onClosePurgeCache,
  } = useDisclosure()

  return (
    <BaseLayout>
      <MainContentHeadingBox>
        <Heading
          as="h5"
          size="md"
          fontWeight="semibold"
          color={colors.appDarkIndigo}
        >
          {t('clear_cache_requests', 'Clear Cache Requests')}
        </Heading>
        <Spacer />

        <PermissionGuard
          permission={PermissionsEnum.SELF_CLEAR_CACHE_MANAGE}
        >
          {
            (hasPermission: boolean) => (
              hasPermission ? (
                <Button onClick={onOpenPurgeCache}>
                  {t('new_request', 'New Request')}
                </Button>
              ) : <></>
            )
          }
        </PermissionGuard>
      </MainContentHeadingBox>
      <MainContentWrapper>
        <PurgeCacheTable
          user={user}
          data={data}
          getData={getData}
          loading={loading}
        />
      </MainContentWrapper>
      <PurgeCacheModal
        isOpen={isOpenPurgeCache}
        onClose={onClosePurgeCache}
        user={user}
        getData={getData}
      />
    </BaseLayout>
  )
}

export default PurgeCacheLayout
